import {
    AnimationState,
    AnimationTypeEnum,
    ApplicationAbstract,
    ApplicationVersionBranch,
    BreakPoint,
    ContainerBlockAbstract,
    DevicePreviewEnum,
    Environment,
    PageStackBlockValueStackMap
} from '@lighthouse/core'
import { ApplicationStruct } from '@lighthouse/core'
import type { AllOtherPlatform, FindUseData } from '@lighthouse/shared'
import { DEFAULT_DESKTOP_BREAK_POINT, DEFAULT_LANG, transformOriginBlock } from '@lighthouse/shared'
import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { atomWithImmer } from 'jotai-immer'
import { find } from 'rambda'

import { userDataAtom } from '../auth/state'
import type { ApplicationModePageHistory } from './constants'
import { APPLICATION_MODE_PAGE_HISTORY, CURRENT_PAGE, developerRole } from './constants'
import type { TemplateComponent } from './type'

export const appListAtom = atomWithImmer<ApplicationAbstract[]>([])

export const appAtom = atomWithImmer<ApplicationAbstract>({ ...ApplicationStruct() })

export const breakPointListAtom = atomWithImmer<BreakPoint[]>([])

export const currentAppIdAtom = atom(get => get(appAtom).id)

export const applicationSettingAtom = atom(get => get(appAtom)?.config)

export const mainBreakPointAtom = atomWithImmer<string>(DEFAULT_DESKTOP_BREAK_POINT)

export const applicationPrivateTemplateAtom = atom(get => get(applicationSettingAtom)?.privateTemplate)

export const otherPlatformListAtom = atomWithImmer<AllOtherPlatform>([])

/** 应用主题色 */
export const applicationThemeColorAtom = atom(get => {
    const { theme } = get(appAtom).config ?? {}
    return theme?.palettes.find(item => item.id === theme.primaryPointer)?.value || ''
})

export const applicationVersionAtom = atom(
    get => get(appAtom).version,
    (get, set, params: Partial<ApplicationVersionBranch>) => {
        const app = get(appAtom)
        set(appAtom, {
            ...app,
            version: {
                ...app.version,
                ...params
            }
        })
        return params
    }
)

export const currentAppVersionIdAtom = atom(get => get(applicationVersionAtom).id)

export const currentEnvIdAtom = atom(get => get(applicationVersionAtom).envId)

export const currentAppOfSpaceIdAtom = atom(get => get(appAtom).spaceId)

/**
 * 按应用存储的断点 ID 映射（localStorage 持久化）
 * key: appId, value: 用户选择的断点 ID
 */
export const breakPointIdMapAtom = atomWithStorage<Record<string, string>>('appBreakPoint', {})

/**
 * 当前断点 ID
 */
export const currentBreakPointIdAtom = atom(get => {
    const appId = get(currentAppIdAtom)
    const selectedMap = get(breakPointIdMapAtom)
    const mainBreakPoint = get(mainBreakPointAtom)
    return selectedMap[appId] || mainBreakPoint
})

/**
 * 当前断点对象（基于选中的断点 ID 和断点列表计算）
 */
export const currentBreakPointAtom = atom(get => {
    const currentBreakPointId = get(currentBreakPointIdAtom)
    const breakPointList = get(breakPointListAtom)
    return find(item => item.id === currentBreakPointId, breakPointList)
})

export const deviceTypeAtom = atom(get => get(currentBreakPointAtom)?.type || DevicePreviewEnum.DESKTOP)
export const isMobileDeviceAtom = atom(get => get(deviceTypeAtom) === DevicePreviewEnum.MOBILE)

export const sharePageAtom = atomWithStorage('sharePage', CURRENT_PAGE)

export const dataDrawerStateAtom = atomWithImmer<boolean>(false)

export const sharePosterAtom = atomWithStorage('sharePoster', 'poster1')

export const appEnvListAtom = atomWithImmer<Environment[]>([])

export const templateBlockAtom = atomWithImmer<TemplateComponent[]>([])

export const applicationModePageHistoryAtom = atomWithImmer<ApplicationModePageHistory>(APPLICATION_MODE_PAGE_HISTORY)

export const appRoleAtom = atom(developerRole.userId)

/** 应用新手指导 */
export const appGuideAtom = atom(get => get(userDataAtom).userCompletedTask ?? [])
/** 应用语言 */
export const languageAtom = atom(DEFAULT_LANG)
/** 查找使用 */
export const findUseAtom = atomWithImmer<FindUseData | undefined>(undefined)
/** 应用发布/更新时状态 */
export const AppPublishStateAtom = atomWithImmer({ isPublish: false, isSuccess: false })
/** 系统页面 */
export const AppSystemPageAtom = atom<string | null>(null)

/** 同步组件 */
export const syncComponentsAtom = atomWithImmer<ContainerBlockAbstract[]>([])

/** 字段block值得映射 */
export const pageStackOfBlockAtom = atomWithImmer<PageStackBlockValueStackMap>({})
/** 修改过的表单容器 */
export const pageStackOfFormContainerBlockChangedAtom = atomWithImmer<Record<string, boolean>>({})
/** 打开的浮层 */
export const pageStackOfFloatBlockAtom = atomWithImmer<
    { stackId: string; blockId: string; recordId?: string; open: boolean; scope?: string }[]
>([])
/** 快速开始的按钮打开状态 */
export const isShowTaskQuickStackAtom = atomWithStorage('isShowTaskQuickStack', true)
/** 播放的动画的scope和blockId */

export const blockAnimationStateAtom = atomWithImmer<
    { scope?: string; blockId: string; played?: boolean; type?: AnimationTypeEnum; state: AnimationState }[]
>([])
/** 同步组件-断点属性合并 */
export const transformSyncComponentsAtom = atom(get => {
    const currentBreakPoint = get(currentBreakPointAtom)
    const mainBreakPoint = get(mainBreakPointAtom)
    const originBlocks = get(syncComponentsAtom)
    return transformOriginBlock(originBlocks, currentBreakPoint?.id || mainBreakPoint, mainBreakPoint)
})

export const pageSyncComponentsAtom = atom(get => {
    const breakPointId = get(currentBreakPointIdAtom)
    const mainBreakPoint = get(mainBreakPointAtom)
    const originSyncBlocks = get(syncComponentsAtom)
    if (!originSyncBlocks) {
        return []
    }
    return transformOriginBlock(originSyncBlocks, breakPointId, mainBreakPoint)
})
